// source: template-project@7007a338a apps/backoffice/src/components/ui/icon-button/IconButton.tsx
import * as React from 'react';
import { Slot, Slottable } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import type { LucideIcon } from 'lucide-react';

import { dsValue } from '@/design-system/tokens';
import { cn } from '@/lib/utils';

/**
 * IconButton — Trafiflow UI v3 (DESIGN_SYSTEM.md §6 "Buttons", §5.2).
 *
 * A square control that holds one glyph. What the paper theme paints by
 * selector today (workspace-ds-test.css §5, §16) is written here as classes:
 *
 *   filled   5% ink at rest with a 1px hairline of the same alpha, 8px radius
 *            (`rounded-sm`), 10% on hover, 20% pressed, ink glyph.
 *   ghost    no resting box; the 5% wash appears on hover.
 *   pressed  an `aria-pressed` toggle that is on keeps the slate primary fill
 *            (`bg-primary text-primary-foreground`), so "on" reads at a glance.
 *   disabled keeps the fill; only the glyph fades to the disabled text colour.
 *
 * Sizes: sm 24 / md 28 / lg 32 px, with a 14 / 16 / 18 px icon (`icon.size.*`)
 * at stroke 1.5 (`icon.stroke.button`). The default is lg, the document's
 * `.tf-icon-btn` (32px, 18px glyph); sm and md are for dense rows. Pass the lucide component as `icon`
 * and the button sizes it from the tokens; the glyph is `aria-hidden`, the
 * required `label` is the accessible name.
 *
 * The name is `aria-label` only. There is no native `title` by default: a
 * title that repeats the name is read twice by several screen readers and
 * never shows on keyboard focus. For a tooltip, wrap the button in the
 * shadcn `Tooltip` (`TooltipTrigger asChild`: the ref is forwarded), or pass
 * `tooltip` for the native one.
 */
const iconButtonVariants = cva(
  'inline-flex shrink-0 items-center justify-center rounded-sm border text-foreground transition-colors duration-150 outline-none select-none focus-visible:border-ds-focus focus-visible:ring-[3px] focus-visible:ring-ds-focus-halo disabled:pointer-events-none disabled:text-ds-disabled-text [&_svg]:pointer-events-none [&_svg]:shrink-0',
  {
    variants: {
      tone: {
        filled: 'border-ds-ink-a05 bg-ds-ink-a05 hover:bg-ds-ink-a10 hover:text-foreground active:bg-ds-ink-a20 disabled:border-ds-ink-a05 disabled:bg-ds-ink-a05',
        ghost: 'border-transparent bg-transparent hover:bg-ds-ink-a05 hover:text-foreground active:bg-ds-ink-a10 disabled:border-transparent disabled:bg-transparent',
      },
      size: {
        sm: 'size-6',
        md: 'size-7',
        lg: 'size-8',
      },
      pressed: {
        true: 'border-transparent bg-primary text-primary-foreground hover:bg-ds-primary-hover hover:text-primary-foreground active:bg-ds-primary-pressed',
        false: '',
      },
    },
    defaultVariants: {
      tone: 'filled',
      size: 'lg',
      pressed: false,
    },
  },
);

export type IconButtonTone = NonNullable<VariantProps<typeof iconButtonVariants>['tone']>;
export type IconButtonSize = NonNullable<VariantProps<typeof iconButtonVariants>['size']>;

/** Glyph size per button size, in px, from the icon tokens: 14 control / 16 menu / 18 button. */
export const ICON_BUTTON_ICON_SIZE: Readonly<Record<IconButtonSize, number>> = {
  sm: Number.parseInt(dsValue('icon.size.control'), 10),
  md: Number.parseInt(dsValue('icon.size.menu'), 10),
  lg: Number.parseInt(dsValue('icon.size.button'), 10),
};

/** Stroke inside buttons (`icon.stroke.button`). */
export const ICON_BUTTON_STROKE = Number(dsValue('icon.stroke.button'));

export interface IconButtonProps
  extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'aria-label' | 'aria-pressed' | 'children'> {
  /** The accessible name (`aria-label`). */
  label: string;
  /** The lucide icon. Sized from the icon tokens and set to stroke 1.5. With `asChild` it is rendered inside the slotted child. */
  icon?: LucideIcon;
  /** The glyph when it is not a lucide component (an `<img>`, a custom SVG). Size it yourself. */
  children?: React.ReactNode;
  tone?: IconButtonTone;
  size?: IconButtonSize;
  /** A toggle's state: `aria-pressed`, `data-state="on|off"` and the primary fill when on. */
  pressed?: boolean;
  /** Also show `label` as the native `title` tooltip. Off by default (see above); a Radix Tooltip is the better tooltip. */
  tooltip?: boolean;
  /** Render the child element (a link, a Radix trigger) with the button's props and classes. */
  asChild?: boolean;
}

const IconButton = React.forwardRef<HTMLButtonElement, IconButtonProps>(
  (
    { className, label, icon: Icon, children, tone = 'filled', size = 'lg', pressed, tooltip = false, asChild = false, type, title, ...props },
    ref,
  ) => {
    const isPressed = pressed === true;
    const glyph = Icon ? <Icon size={ICON_BUTTON_ICON_SIZE[size]} strokeWidth={ICON_BUTTON_STROKE} aria-hidden="true" /> : null;
    const shared = {
      ref,
      'data-slot': 'icon-button',
      'data-variant': tone,
      'data-size': size,
      'data-state': pressed === undefined ? undefined : isPressed ? 'on' : 'off',
      'aria-label': label,
      'aria-pressed': pressed === undefined ? undefined : isPressed,
      title: title ?? (tooltip ? label : undefined),
      className: cn(iconButtonVariants({ tone, size, pressed: isPressed }), className),
      ...props,
    } as const;
    if (asChild) {
      // The single child IS the element (Slot merges the props onto it); `Slottable`
      // marks it so the glyph renders inside that child, sized by the tokens as usual.
      return (
        <Slot {...shared}>
          <Slottable>{children}</Slottable>
          {glyph}
        </Slot>
      );
    }
    return (
      <button {...shared} type={type ?? 'button'}>
        {glyph ?? children}
      </button>
    );
  },
);
IconButton.displayName = 'IconButton';

export { IconButton, iconButtonVariants };
